import { EntityIcon } from '../SearchIcons'
import { SearchPopoverSectionHeader } from '../SearchPopoverParts'
import {
  getSectionHeaderKey,
  getSectionToggleAction,
  getSectionToggleKey,
} from '../../logic/globalSearchSections'
import type { RenderedSectionRows, SearchSelection, Section, ToggleAction } from '../../logic/types'
import {
  GlobalSearchPlaceholderRow,
  GlobalSearchResultRow,
  GlobalSearchSectionToggle,
} from './GlobalSearchResultRows'

interface GlobalSearchSectionProps {
  section: Section
  sectionIndex: number
  isAllTab: boolean
  isCollapsed: boolean
  isExpanded: boolean
  rendered: RenderedSectionRows
  activeIndex: number
  /** Each header's, row's, and toggle's place in the All tab's arrow-key order. */
  indexOf: ReadonlyMap<string, number>
  onActivate: (index: number) => void
  onSelect: (selection: SearchSelection) => void
  onToggleCollapsed: (sectionKey: string) => void
  onToggleRows: (
    section: Section,
    action: ToggleAction,
    options: { focusToggleAfterCollapse: boolean }
  ) => void
}

/**
 * One result section. On the All tab it has a collapsible header and a "See
 * more" / "See less" toggle; a result tab renders only its virtualized window,
 * with spacers for the rows above and below.
 */
export function GlobalSearchSection({
  section,
  sectionIndex,
  isAllTab,
  isCollapsed,
  isExpanded,
  rendered,
  activeIndex,
  indexOf,
  onActivate,
  onSelect,
  onToggleCollapsed,
  onToggleRows,
}: GlobalSearchSectionProps) {
  const headerIndex = indexOf.get(getSectionHeaderKey(section.key)) ?? -1
  const toggleAction = isAllTab ? getSectionToggleAction(section) : null
  const toggleIndex = toggleAction
    ? (indexOf.get(getSectionToggleKey(section.key, toggleAction)) ?? -1)
    : -1

  return (
    <section className={isAllTab && sectionIndex > 0 ? 'mt-3' : undefined}>
      {isAllTab && (
        <SearchPopoverSectionHeader
          title={section.title}
          count={section.count}
          active={headerIndex === activeIndex}
          collapsed={isCollapsed}
          icon={<EntityIcon kind={section.kind} className="shrink-0 text-subtle" />}
          style={{ zIndex: 30 + sectionIndex }}
          onPointerEnter={() => onActivate(headerIndex)}
          onClick={() => onToggleCollapsed(section.key)}
        />
      )}
      {!isCollapsed && (
        <div className={isAllTab ? 'mt-1' : undefined}>
          {rendered.beforeHeight > 0 && (
            <div
              className="[overflow-anchor:none]"
              style={{ height: rendered.beforeHeight }}
              aria-hidden="true"
            />
          )}
          {rendered.rows.map(({ row, index }) =>
            row ? (
              <GlobalSearchResultRow
                key={row.key}
                row={row}
                active={index === activeIndex}
                onActivate={() => onActivate(index)}
                onSelect={onSelect}
              />
            ) : (
              <GlobalSearchPlaceholderRow
                key={`placeholder:${section.kind}:${index}`}
                active={index === activeIndex}
                onActivate={() => onActivate(index)}
              />
            )
          )}
          {rendered.afterHeight > 0 && (
            <div
              className="[overflow-anchor:none]"
              style={{ height: rendered.afterHeight }}
              aria-hidden="true"
            />
          )}
          {toggleAction && (
            <GlobalSearchSectionToggle
              action={toggleAction}
              isExpanded={isExpanded}
              active={toggleIndex === activeIndex}
              onActivate={() => onActivate(toggleIndex)}
              onToggle={() =>
                onToggleRows(section, toggleAction, { focusToggleAfterCollapse: isExpanded })
              }
            />
          )}
        </div>
      )}
    </section>
  )
}
